import React, { useState, useEffect, } from 'react'
import { Tag, Table, Space, Row, Col, Input, Typography, Button, Popconfirm, Pagination } from 'antd';
import { ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, SyncOutlined, SearchOutlined, ExclamationCircleOutlined, DeleteOutlined,} from '@ant-design/icons';
import { deleteSchedule } from '../../../services/UpdateManagerService';
import utc from "dayjs/plugin/utc";
import dayjs from "dayjs";
import { useNotificationApi } from '../../UI/NotificationProvider';

const { Text, Paragraph, } = Typography;

const currentTimeStyle = {
    fontSize: 14, 
    fontWeight: 800, 
    letterSpacing: 0.5, 
    padding: '4px 15px',
    margin: 0,
};

dayjs.extend(utc);

export const UpdateManagerTable = ({dataSource, setDataSource, pagination, setPagination, search, setSearch, refreshHandler, ...other}) => {

    const notificationApi = useNotificationApi();

    const [currentTime, setCurrentTime] = useState(new Date().toLocaleString().replace(',',''));
    const [currentUTCTime, setCurrentUTCTime] = useState(new Date().toUTCString().replace(',',''));
    const [displayUTCTime, setDisplayUTCTime] = useState(false);
    const [displayScheduleDescription, setDisplayScheduleDescription] = useState(false);

    const dateFormat = 'MM/DD/YYYY hh:mm:ss A';

    useEffect(() => {
        let secTimer = setInterval(() => {
            setCurrentTime(new Date().toLocaleString().replace(',',''))
            setCurrentUTCTime(new Date().toUTCString().replace(',',''))
        }, 1000)
        return () => clearInterval(secTimer);
    }, []);

    const toLocalString = (utcDate, format = dateFormat) => {
        return dayjs.utc(utcDate).local().format(dateFormat);
    }

    const toUTCString = (utcDate, format = dateFormat) => {
        return dayjs.utc(utcDate).format(dateFormat);
    }

    const statusTag = (record) => {

        let {sentOn, scheduledFor, } = record;
        let color;
        let icon;

        const now = dayjs();
        const checkSchedule = dayjs.utc(scheduledFor, 'YYYY-MM-DD hh:mm:ss A').isAfter(now);

        if(!sentOn && checkSchedule){
            sentOn = "Pending"
            color = "warning";
            icon = <SyncOutlined spin />;
        } else if (sentOn === null){
            sentOn = 'File not downloaded'
            color = "red";
            icon = <CloseCircleOutlined />;
        } else{
            sentOn = dayjs(sentOn).format(dateFormat);
            color = "green";
            icon = <CheckCircleOutlined />;
        }
        return (
            <>
                <Tag bordered={false} color={color} icon={icon} key="sentOn">{sentOn}</Tag>
                {checkSchedule ? 
                    <Popconfirm
                        title='Are you sure you want to cancel this schedule?'
                        onConfirm={() => deleteScheduleHandler(record)}
                        okText="Yes"
                        cancelText="No"
                        okButtonProps={{type: 'primary', danger: true}}
                        icon={<ExclamationCircleOutlined style={{color: 'var(--error-color)'}}/>}
                    >
                        <Button danger type='primary' size='small' icon={<DeleteOutlined/>}/> 
                    </Popconfirm>
                : 
                null}
            </>
        );
    };

    const deleteScheduleHandler = (schedule) => {

        let now = dayjs();
        let checkSchedule = dayjs.utc(schedule.scheduledFor, 'YYYY-MM-DD hh:mm:ss A').isAfter(now);

        if(checkSchedule){
            deleteSchedule({scheduledUpdateID: schedule.id, delete: true}).then( function(response) {
                if(response){
                    refreshHandler();
                    setTimeout(() => {
                        notificationApi['success']({
                            message: 'Schedule Deleted',
                            description: (<>
                                <Paragraph>
                                    Serial Number: {schedule.owner?.serialNum}<br/>
                                    Device Type: {schedule.owner?.deviceType}<br/>
                                    Scheduled For: {dayjs(schedule.scheduledFor).format(dateFormat)}<br/>
                                </Paragraph>
                            </>),
                            duration: 12,
                            style: {
                                width: 500,
                            },
                        });
                    }, 1500);
                }
            })
        } else {
            notificationApi['error']({
                message: 'Oops!',
                description: (<>
                    <Paragraph>
                        This event has been already scheduled.<br/>
                        Serial Number: {schedule.owner?.serialNum}<br/>
                        Device Type: {schedule.owner?.deviceType}<br/>
                        Scheduled For: {dayjs(schedule.scheduledFor).format(dateFormat)}<br/>
                    </Paragraph>
                </>),
                duration: 12,
                style: {
                    width: 500,
                },
            });
        }
    }

    // const differenceInMinutes = ( scheduledFor ) => {
    //     let now = moment(new Date()); //todays date
    //     let date = moment(scheduledFor); // another date
    //     let duration = moment.duration(date.diff(now));
    //     let minutes = duration.asMinutes();

    //     return minutes
    // }

    const columns = [
        {
            title: 'Serial Number',
            dataIndex: 'owner',
            key: 'owner',
            render: (value, _) => value.serialNum,
            sorter: (a, b) => a.owner?.serialNum?.localeCompare(b.owner?.serialNum),
        },
        {
            title: 'File Type',
            dataIndex: 'owner',
            key: 'deviceType',
            render: (value) => value.deviceType,
            sorter: (a, b) => a.owner?.deviceType?.localeCompare(b.owner?.deviceType),
        },
        {
            title: 'File',
            dataIndex: 'fileName',
            key: 'fileName',
            ellipsis: true,
            sorter: (a, b) => a.fileName?.localeCompare(b.fileName),
        },
        {
            title: 'Description',
            dataIndex: 'comment',
            key: 'comment',
            hidden: !displayScheduleDescription,
            render: (_, record) => record.owner.comment,
        },
        {
            title: 'Scheduled For (UTC)',
            dataIndex: 'scheduledFor',
            key: 'scheduledFor',
            hidden: !displayUTCTime,
            sorter: (a, b) => a.scheduledFor?.localeCompare(b.scheduledFor),
            render: (value) => toUTCString(value)
        },
        {
            title: 'Scheduled For (Local Time)',
            dataIndex: 'scheduledFor',
            key: 'scheduledFor',
            sorter: (a, b) => a.scheduledFor?.localeCompare(b.scheduledFor),
            render: (value) => toLocalString(value)
        },
        {
            title: 'Sent On',
            dataIndex: 'sentOn',
            key: 'sentOn',
            
            render: (_, record) => {
                return (
                    <>
                        <Space size='small'>
                            <p>{statusTag(record)}</p>
                        </Space>
                    </>
                )
            }
        },
    ].filter(item => !item.hidden);


    const onShowSizeChange = (current, pageSize) => {
        const change = {
            current: current, 
            pageSize: pageSize,
        };        
        setPagination(change);
    };

    const onChange = (page, pageSize) => {
        const change = {
            current: page, 
            pageSize: pageSize,
        };
        setPagination(change);
    };

    const showTotalMessage = (total, range) => {

        let showTotal;

        if(range[0] >= 0 && range[1] >= 0){
            if(search === '') {
                showTotal = `Showing ${range[0]}-${range[1]} items. Total ${dataSource?.scheduledUpdates?.count} entries.`;
            } else if(search && dataSource?.scheduledUpdates?.count > 0){
                showTotal = `${dataSource?.scheduledUpdates?.count} result${dataSource?.scheduledUpdates?.count > 1 ? 's' : ''} containing "${search}".`
            } else if (search && !dataSource?.scheduledUpdated){
                showTotal = `No matching results containing "${search}".`
            }
        }
        return showTotal;
    }

    return (
        <>
            <div>
                <Row justify="space-between" style={{marginBottom: 30, gap:"30px",}}>
                    <Col>
                        <Space size="small">
                            <p>Search:</p><Input placeholder="input search text" suffix={<SearchOutlined/>} allowClear onChange={(e) => setSearch(e.target.value)} value={search}/>
                            { search ? <Button type='primary' onClick={()=> setSearch('')}>Clear</Button> : null }
                        </Space>
                    </Col>
                    <Col>
                        <Space size='small' wrap>
                            <Tag 
                                icon={<ClockCircleOutlined />} 
                                color="default"
                                style={currentTimeStyle}
                                >
                                {displayUTCTime ? 'UTC' : 'Local'} Time: <Text type='secondary'>{displayUTCTime ? currentUTCTime : currentTime}</Text>
                            </Tag> 
                            <Button onClick={() => setDisplayUTCTime(!displayUTCTime)}>{displayUTCTime ? 'Hide' : 'Display'} UTC Time</Button>
                            <Button onClick={() => setDisplayScheduleDescription(!displayScheduleDescription)}>{displayScheduleDescription ? 'Hide' : 'Display'} Description</Button>
                            <Button onClick={() => refreshHandler()} icon={<SyncOutlined/>}>Refresh</Button>
                        </Space>
                    </Col>
                </Row>

                <Table
                    size="middle"
                    columns = {columns}
                    dataSource = {dataSource?.scheduledUpdates?.rows}
                    scroll={{
                        x: "1200",
                    }}
                    rowKey='id'
                    tableLayout="auto"
                    pagination={false}
                >
                </Table>

                {pagination ? 
                    <Row justify="end" style={{marginTop: 20}}>
                        <Pagination 
                            total={dataSource?.scheduledUpdates?.count}
                            defaultPageSize={pagination.pageSize}
                            current={pagination.current}
                            showSizeChanger
                            showQuickJumper
                            showTotal = {(total, range) => showTotalMessage(total, range)}
                            onChange={(page, pageSize) => onChange(page, pageSize)}
                            onShowSizeChange={(current, size) => onShowSizeChange(current, size)}
                        />
                    </Row> 
                : ''}

                {search && dataSource?.scheduledUpdates?.rows.length === 0 ?
                <p style={{marginTop: 30}}>0 matching results.</p> : '' 
            }
            </div>
            
        </>
    );
}